import { useState, useEffect } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { format } from 'date-fns';
import CurrencyInput from '../components/CurrencyInput';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n ?? 0);
const today = () => new Date().toISOString().split('T')[0];

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid var(--stone)', borderRadius: 8, padding: '10px 14px', boxShadow: 'var(--shadow)' }}>
      <div style={{ fontSize: 11, color: 'var(--mid)', marginBottom: 4 }}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ fontSize: 12.5, color: p.color }}>{p.name}: {fmt(p.value)}</div>
      ))}
    </div>
  );
};

export default function NetWorthPage() {
  const [live, setLive] = useState(null);
  const isMobile = useIsMobile();
  const [snapshots, setSnapshots] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ date: today(), cash: '', investments: '', other_assets: '', total_liabilities: '', notes: '' });
  const [usd, setUsd] = useState({ cash: 0, investments: 0, other_assets: 0, total_liabilities: 0 });

  const setUsdField = (k, v) => setUsd(u => ({ ...u, [k]: v }));
  const getUSD = (k) => usd[k] > 0 ? usd[k] : (parseFloat(form[k]) || 0);

  const load = async () => {
    const [lv, snaps] = await Promise.all([api.getLiveNetWorth(), api.getNetWorthSnapshots()]);
    setLive(lv);
    setSnapshots(snaps);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.addNetWorthSnapshot({
        date: form.date,
        cash: getUSD('cash'),
        investments: getUSD('investments'),
        other_assets: getUSD('other_assets'),
        total_liabilities: getUSD('total_liabilities'),
        notes: form.notes || null,
      });
      toast.success('Snapshot saved!');
      setShowModal(false);
      setForm({ date: today(), cash: '', investments: '', other_assets: '', total_liabilities: '', notes: '' });
      load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    await api.deleteNetWorthSnapshot(id);
    setSnapshots(s => s.filter(x => x.id !== id));
  };

  const chartData = snapshots.map(s => ({
    name: format(new Date(s.date), 'MMM yy'),
    'Net Worth': s.net_worth,
    Assets: s.cash + s.investments + s.other_assets,
    Liabilities: s.total_liabilities,
  }));

  const nwColor = (live?.net_worth ?? 0) >= 0 ? 'var(--green)' : 'var(--red)';

  return (
    <div className="animate-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="page-eyebrow">Wealth</div>
          <div className="page-title">Net Worth</div>
          <div className="page-subtitle" style={{ marginBottom: 0 }}>Assets minus liabilities — the number that matters most</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Log Snapshot</button>
      </div>

      {/* Live estimate from existing data */}
      {live && (
        <div className="card mb-6" style={{ borderLeft: '3px solid var(--stone-mid)', padding: '20px 24px' }}>
          <div className="section-title mb-3">Live Estimate — from your existing data</div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 20 }}>
            <div>
              <div className="stat-label">Cash</div>
              <div style={{ fontSize: 20, fontWeight: 500, color: live.cash != null ? 'var(--ink)' : 'var(--mid)' }}>
                {live.cash != null ? fmt(live.cash) : 'No data'}
              </div>
              {live.cash_date && <div className="stat-sub">as of {format(new Date(live.cash_date), 'MMM d')}</div>}
            </div>
            <div>
              <div className="stat-label">Investments</div>
              <div style={{ fontSize: 20, fontWeight: 500, color: live.investments != null ? 'var(--blue)' : 'var(--mid)' }}>
                {live.investments != null ? fmt(live.investments) : 'No data'}
              </div>
              {live.investments_date && <div className="stat-sub">as of {format(new Date(live.investments_date), 'MMM d')}</div>}
            </div>
            <div>
              <div className="stat-label">Total Debt</div>
              <div style={{ fontSize: 20, fontWeight: 500, color: 'var(--red)' }}>{fmt(live.total_debt)}</div>
              <div className="stat-sub">{live.loan_count} loan{live.loan_count !== 1 ? 's' : ''}</div>
            </div>
            <div>
              <div className="stat-label">Estimated Net Worth</div>
              <div style={{ fontSize: 26, fontFamily: 'var(--font-d)', fontStyle: 'italic', color: live.net_worth != null ? nwColor : 'var(--mid)' }}>
                {live.net_worth != null ? fmt(live.net_worth) : '—'}
              </div>
              {!live.data_complete && <div className="stat-sub" style={{ color: 'var(--gold)' }}>Add cash & investments for full picture</div>}
            </div>
          </div>
        </div>
      )}

      {/* Chart */}
      {snapshots.length > 1 && (
        <div className="card mb-6">
          <div className="section-title mb-4">Net Worth Over Time</div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="nwGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--green)" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="var(--green)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="name" tick={{ fill: 'var(--mid)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="Net Worth" stroke="var(--green)" strokeWidth={2} fill="url(#nwGrad)" dot={{ r: 3, fill: 'var(--green)' }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Snapshots table */}
      {snapshots.length > 0 && (
        <div className="card" style={{ padding: 0 }}>
          <table>
            <thead>
              <tr>
                <th>Date</th><th style={{ textAlign: 'right' }}>Cash</th>
                <th style={{ textAlign: 'right' }}>Investments</th>
                <th style={{ textAlign: 'right' }}>Other Assets</th>
                <th style={{ textAlign: 'right' }}>Liabilities</th>
                <th style={{ textAlign: 'right' }}>Net Worth</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {[...snapshots].reverse().map(s => (
                <tr key={s.id}>
                  <td style={{ color: 'var(--dim)', fontSize: 12 }}>{format(new Date(s.date), 'MMM d, yyyy')}</td>
                  <td className="text-right">{fmt(s.cash)}</td>
                  <td className="text-right" style={{ color: 'var(--blue)' }}>{fmt(s.investments)}</td>
                  <td className="text-right" style={{ color: 'var(--dim)' }}>{fmt(s.other_assets)}</td>
                  <td className="text-right amount-neg">−{fmt(s.total_liabilities)}</td>
                  <td className="text-right" style={{ fontWeight: 600, color: s.net_worth >= 0 ? 'var(--green)' : 'var(--red)', fontFamily: 'var(--font-d)', fontStyle: 'italic', fontSize: 15 }}>{fmt(s.net_worth)}</td>
                  <td><button className="btn btn-sm btn-danger" onClick={() => del(s.id)}>×</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {snapshots.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '48px', borderStyle: 'dashed' }}>
          <div style={{ fontSize: 28, marginBottom: 10 }}>≋</div>
          <div style={{ fontWeight: 500, marginBottom: 5 }}>No snapshots yet</div>
          <div style={{ color: 'var(--mid)', fontSize: 13 }}>Log monthly snapshots to track your net worth trajectory over time</div>
        </div>
      )}

      {showModal && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 460 }}>
            <div className="modal-title">Log Net Worth Snapshot</div>
            <div className="form-group">
              <label className="form-label">Date</label>
              <input className="input" type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
            </div>
            {[
              ['cash', 'Cash & Bank Accounts'],
              ['investments', 'Investment Portfolio'],
              ['other_assets', 'Other Assets — home, car, etc.'],
              ['total_liabilities', 'Total Liabilities — all debt'],
            ].map(([k, label]) => (
              <div className="form-group" key={k}>
                <label className="form-label">{label}</label>
                <CurrencyInput
                  value={form[k]}
                  onChange={(raw, usdVal) => {
                    setForm(f => ({ ...f, [k]: raw }));
                    setUsdField(k, usdVal);
                  }}
                />
              </div>
            ))}
            {/* Live preview */}
            <div className="card-off" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <span style={{ fontSize: 13, color: 'var(--dim)' }}>Net worth preview</span>
              <span style={{ fontFamily: 'var(--font-d)', fontStyle: 'italic', fontSize: 18, color: 'var(--ink)' }}>
                {fmt(getUSD('cash') + getUSD('investments') + getUSD('other_assets') - getUSD('total_liabilities'))}
              </span>
            </div>
            <div className="flex gap-3 justify-between">
              <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Snapshot'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
